import { useEffect, useState } from "react";
import {
  AddIcon,
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  RepeatIcon
} from "@chakra-ui/icons";
import { GetLayout } from "components/get-layout";
import { useApplicationContext } from "contexts/ApplicationContext";
import { Box, Button, Grid, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { CustomModal } from "components/custom-modal";
import DynamicTable from "components/dynamic-table";
import { FaKey, FaPowerOff } from "react-icons/fa";
import { openModalConfirm } from "components/modal-confirm-new";
import { isColomboUser } from "utils/is-colombo-user";
import { allowedUserTypesLoginColombo } from "components/login-page/functions";
import { openModalError } from "components/modal-error";
import { objectSchema } from "utils/object-methods";
import { fieldValidation } from "utils/field-validation";
import { User } from "contexts/types/application-context-types";
import { InputField } from "components/input-field";
import { DropdownField } from "components/dropdown-field";
import { getErrorMessage } from "utils/util";
import { ModalComDropdownRecorrencia } from "components/servicos-components/modal-datafast/modal-renovacao-fila";
import { Column } from "components/dynamic-table/types";
import { Toast } from "components/toast";
import { RoutePermissionFunction } from "router-manager/parts";
import { apiJWT } from "api/api-jwt";

interface UserListRow {
  id: number;
  username: string;
  password: null | string;
  enabled: boolean;
  type: User["userData"]["type"] | "";
  name: string;
  customer: {
    id: number | string;
    enabled?: boolean;
    name?: string;
    cnpj?: null | string;
    codigoNova?: null | string;
    email?: null | string;
    telefone?: null | string;
    enabled3cplus?: null | string;
    token3cplus?: null | string;
    comercialUser?: null | string;
    regionalCustomer?: null | string;
  };
  userId3cplus: 44607;
}

const Usuarios = () => {
  const { user } = useApplicationContext();
  const { userData } = user;
  const [usuarioDialog, setUsuarioDialog] = useState<Partial<UserListRow>>({});
  const [usuarioPasswordDialog, setUsuarioPasswordDialog] = useState<{
    id?: number;
    password?: string;
  }>({});
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>(
    []
  );
  const [filtros, setFiltros] = useState({ nomeOrCodigo: "" });
  const [openUsuarioDialog, setOpenUsuarioDialog] = useState(false);
  const [openUsuarioPasswordDialog, setOpenUsuarioPasswordDialog] =
    useState(false);
  const [usuarios, setUsuarios] = useState<UserListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [errors, setErrors] = useState<{
    customer?: boolean;
    name?: boolean;
    username?: boolean;
    password?: boolean;
    type?: boolean;
  }>({
    customer: false,
    name: false,
    username: false,
    password: false,
    type: false
  });

  const isUpdate = !!usuarioDialog.id;
  const isParceiroMaster = userData.type === "PARCEIRO_MASTER";

  const onOpenUsuarioModal = () => {
    setOpenUsuarioDialog(true);
    if (isParceiroMaster && customers.length === 1) {
      setUsuarioDialog((usuario) => ({ ...usuario, customer: customers[0] }));
    }
  };

  const onCloseUsuarioModal = () => {
    setOpenUsuarioDialog(false);
    setSaving(false);
  };

  const getFilteredUsuarios = () => {
    let filteredUsuarios = [...usuarios];
    if (filtros.nomeOrCodigo.trim() !== "") {
      filteredUsuarios = filteredUsuarios.filter(
        (c) =>
          (c.name || "")
            .toUpperCase()
            .includes(filtros.nomeOrCodigo.trim().toUpperCase()) ||
          (c.username || "")
            .toUpperCase()
            .includes(filtros.nomeOrCodigo.trim().toUpperCase())
      );
    }
    return filteredUsuarios;
  };

  const onChangeFiltro = (
    field: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFiltros({ ...filtros, [field]: e.target.value });
  };

  const saveOrUpdateUsuarioPassword = async () => {
    const { fieldsErrors } = objectSchema(
      {
        password: (value) => {
          const { isValid } = fieldValidation({ value }).required();
          return { valid: isValid };
        }
      },
      usuarioPasswordDialog
    );

    if (fieldsErrors.includes("password")) return setErrors({ password: true });

    let tempUsuarioPassword = {
      ...usuarioPasswordDialog,
      password: usuarioPasswordDialog.password
        ? usuarioPasswordDialog.password.trim()
        : ""
    };
    setUpdatingPassword(true);
    try {
      await apiJWT.patch(
        "/usuarios/" + tempUsuarioPassword.id + "/password",
        tempUsuarioPassword
      );
    } catch (error: any) {
      Toast({
        title: getErrorMessage({
          error,
          defaultMessage: "Erro ao alterar senha do usuário!"
        }),
        status: "error"
      });
    }
    await loadUsuarios();
    setOpenUsuarioPasswordDialog(false);
    setUpdatingPassword(false);
  };

  const validateUsuarioSchema = () => {
    const { fieldsErrors, isValid } = objectSchema(
      {
        customer: {
          id: (value) => {
            const { isValid } = fieldValidation({ value }).required();
            return { valid: isValid };
          }
        },
        name: (value) => {
          const { isValid } = fieldValidation({ value }).required();
          return { valid: isValid };
        },
        username: (value) => {
          const { isValid } = fieldValidation({ value }).required();
          return { valid: isValid };
        },
        password: (value) => {
          const { isValid } = fieldValidation({
            value: value?.trim?.()
          }).required();
          return { valid: isUpdate ? true : isValid };
        },
        type: (value) => {
          const { isValid } = fieldValidation({ value }).required();
          return { valid: isValid };
        }
      },
      usuarioDialog
    );
    let errors: any = {};
    (fieldsErrors as string[]).forEach((key) => {
      if (key === "id") key = "customer";
      errors[key] = true;
    });
    return { isValid, errors };
  };

  const saveOrUpdateUsuario = async () => {
    const { isValid, errors } = validateUsuarioSchema();

    if (!isValid) return setErrors(errors);

    openModalConfirm({
      message: `Deseja salvar?`,
      onConfirm: async () => {
        let body = {
          ...usuarioDialog,
          name: usuarioDialog.name?.trim(),
          username: usuarioDialog.username?.trim(),
          password: usuarioDialog.password ? usuarioDialog.password.trim() : ""
        };

        setSaving(true);
        try {
          const { data } = isUpdate
            ? await apiJWT.put(`/usuarios/${body.id}`, body)
            : await apiJWT.post("/usuarios?novo=true", body);
          onCloseUsuarioModal();
          if (isUpdate) updateRow(data);
          else addUserRow(data);
        } catch (error: any) {
          const tag = error?.response?.data?.tag;
          const errorMessage: any = {
            username_exists: "Usuário já cadastrado",
            number_of_contracted_accounts_already_reached:
              "Não é possivel criar o usuário! Verifique a quantidade de contas contratadas no menu carteira.",
            customer_already_has_registered_user:
              "O parceiro já possui usuário cadastrado"
          };
          openModalError({
            title: "Atenção",
            message: getErrorMessage({
              error,
              defaultMessage: errorMessage[tag] ?? "Erro ao salvar usuário!"
            })
          });
        } finally {
          setSaving(false);
        }
      },
      confirmButtonStyle: { variant: undefined }
    });
  };

  const atualizar = async () => {
    setLoading(true);
    await loadCustomers();
    await loadUsuarios();
    setLoading(false);
  };

  useEffect(() => {
    atualizar();
  }, []);

  const loadUsuarios = async () => {
    try {
      let response = await apiJWT.get("/usuarios");
      setUsuarios(response.data);
    } catch (error) {
      Toast({
        title: "Erro ao carregar usuários",
        status: "error"
      });
    }
  };

  const loadCustomers = async () => {
    try {
      let response = await apiJWT.get("/customers");
      setCustomers(response.data);
    } catch (error) {
      Toast({
        title: "Não foi possível carregar as empresas",
        status: "error"
      });
    }
  };

  const acceptDeleteUsuario = async (rowData: UserListRow) => {
    try {
      await apiJWT.delete("/usuarios/" + rowData.id);
    } catch (error: any) {
      Toast({
        title: getErrorMessage({
          error,
          defaultMessage: "Erro ao excluir Usuário!"
        }),
        status: "error"
      });
    }
    await loadUsuarios();
  };

  const onChangeUsuarioDialog = (field: string, value: any) => {
    setErrors({ ...errors, [field]: false });
    if (["username", "password"].includes(field)) {
      setUsuarioDialog({ ...usuarioDialog, [field]: value.trim() });
    } else if ("customer" === field) {
      setUsuarioDialog({ ...usuarioDialog, customer: { id: value } });
    } else {
      setUsuarioDialog({ ...usuarioDialog, [field]: value });
    }
  };

  const onChangeUsuarioPasswordDialog = (field: string, value: any) => {
    setErrors({ ...errors, [field]: false });
    setUsuarioPasswordDialog({
      ...usuarioPasswordDialog,
      [field]: value.trim()
    });
  };

  const openDialogNewUsuario = () => {
    setErrors({
      customer: false,
      name: false,
      username: false,
      password: false,
      type: false
    });
    setUsuarioDialog({
      enabled: true,
      customer: { id: "" },
      name: "",
      type: "",
      username: "",
      password: ""
    });
    onOpenUsuarioModal();
  };

  const editUsuario = (usuario: UserListRow) => {
    setErrors({
      customer: false,
      name: false,
      username: false,
      password: false,
      type: false
    });
    setUsuarioDialog({ ...usuario });
    onOpenUsuarioModal();
  };

  const updateRow = (newRow: UserListRow) => {
    setUsuarios((list) => {
      return list.map((currRow) => {
        if (newRow.id === currRow.id) return newRow;
        return currRow;
      });
    });
  };

  const addUserRow = (newRow: UserListRow) => {
    setUsuarios([...usuarios, newRow]);
  };

  const editUsuarioPassword = (usuario: UserListRow) => {
    setErrors({ password: false });
    setUsuarioPasswordDialog({ ...usuario, password: "" });
    setOpenUsuarioPasswordDialog(true);
  };

  const getDescOfType = (type: string) => {
    if ("SUPER" === type) return "Super";
    else if ("FINANCEIRO" === type) return "Financeiro";
    else if ("ASCOM" === type) return "ASCOM";
    else if ("REGIONAL" === type) return "Regional";
    else if ("COMERCIAL" === type) return "Comercial";
    else if ("PARCEIRO_GERENTE_ESTRATEGICO" === type)
      return "Gerente (Estratégico)";
    else if ("PARCEIRO_GERENTE_VAREJO" === type) return "Gerente (Varejo)";
    else if ("PARCEIRO_MASTER" === type) return "Parceiro (Master)";
    else if ("PARCEIRO_VENDEDOR" === type) return "Parceiro (Vendedor)";
    else if ("PARCEIRO_OPERACIONAL" === type) return "Parceiro (Operacional)";
    else return "";
  };

  const getDropdownUserType = () => {
    if (isUpdate && !canEdit()) {
      return [
        { name: "Super", value: "SUPER" },
        { name: "Regional", value: "REGIONAL" },
        { name: "Comercial", value: "COMERCIAL" },
        {
          name: "Gerente (Estratégico)",
          value: "PARCEIRO_GERENTE_ESTRATEGICO"
        },
        { name: "Gerente (Varejo)", value: "PARCEIRO_GERENTE_VAREJO" },
        { name: "Parceiro (Master)", value: "PARCEIRO_MASTER" },
        { name: "Parceiro (Vendedor)", value: "PARCEIRO_VENDEDOR" },
        { name: "Parceiro (Operacional)", value: "PARCEIRO_OPERACIONAL" }
      ];
    } else {
      const userTypes: any = {
        SUPER: [
          { name: "Regional", value: "REGIONAL" },
          { name: "Comercial", value: "COMERCIAL" },
          { name: "Parceiro (Master)", value: "PARCEIRO_MASTER" },
          { name: "Parceiro (Vendedor)", value: "PARCEIRO_VENDEDOR" },
          { name: "Parceiro (Operacional)", value: "PARCEIRO_OPERACIONAL" }
        ],
        REGIONAL: [
          { name: "Comercial", value: "COMERCIAL" },
          { name: "Parceiro (Master)", value: "PARCEIRO_MASTER" },
          { name: "Parceiro (Vendedor)", value: "PARCEIRO_VENDEDOR" },
          { name: "Parceiro (Operacional)", value: "PARCEIRO_OPERACIONAL" }
        ],
        COMERCIAL: isColomboUser(user)
          ? [
              { name: "Parceiro (Master)", value: "PARCEIRO_MASTER" },
              { name: "Parceiro (Vendedor)", value: "PARCEIRO_VENDEDOR" }
            ]
          : [{ name: "Parceiro (Master)", value: "PARCEIRO_MASTER" }],
        PARCEIRO_MASTER: [
          { name: "Parceiro (Vendedor)", value: "PARCEIRO_VENDEDOR" },
          { name: "Parceiro (Operacional)", value: "PARCEIRO_OPERACIONAL" }
        ],
        PARCEIRO_GERENTE_ESTRATEGICO: [
          { name: "Parceiro (Master)", value: "PARCEIRO_MASTER" },
          { name: "Parceiro (Vendedor)", value: "PARCEIRO_VENDEDOR" }
        ],
        PARCEIRO_GERENTE_VAREJO: [
          { name: "Parceiro (Master)", value: "PARCEIRO_MASTER" },
          { name: "Parceiro (Vendedor)", value: "PARCEIRO_VENDEDOR" }
        ]
      };
      if (isColomboUser(user)) {
        const allowedTypesList = userTypes[userData.type ?? ""];
        return allowedTypesList?.filter(
          (curr: { name: string; value: User["userData"]["type"] }) =>
            allowedUserTypesLoginColombo.includes(curr.value) // Verifica os user types que a colombo tem roteamento de login configurado
        );
      } else {
        return userTypes[userData.type ?? ""];
      }
    }
  };

  const hideUsuarioPasswordDialog = () => {
    setOpenUsuarioPasswordDialog(false);
  };

  const usuarioDialogFooter = (
    <>
      <Button
        leftIcon={<CheckIcon />}
        className="p-button-text"
        onClick={saveOrUpdateUsuario}
        isLoading={saving}
        loadingText="Salvando"
      >
        Salvar
      </Button>
      <Button
        leftIcon={<CloseIcon w="12px" h="12px" />}
        variant="outline"
        onClick={onCloseUsuarioModal}
      >
        Cancelar
      </Button>
    </>
  );

  const usuarioPasswordDialogFooter = (
    <>
      <Button
        leftIcon={<CheckIcon />}
        onClick={saveOrUpdateUsuarioPassword}
        isLoading={updatingPassword}
        loadingText="Salvando"
      >
        Salvar
      </Button>
      <Button
        leftIcon={<CloseIcon w="12px" h="12px" />}
        onClick={hideUsuarioPasswordDialog}
        variant="outline"
      >
        Cancelar
      </Button>
    </>
  );

  const canEdit = () => {
    if (usuarioDialog.id) {
      if (
        "SUPER" === userData.type &&
        [
          "REGIONAL",
          "FINANCEIRO",
          "COMERCIAL",
          "PARCEIRO_MASTER",
          "PARCEIRO_VENDEDOR",
          "PARCEIRO_OPERACIONAL"
        ].includes(usuarioDialog.type as string)
      ) {
        return true;
      } else if (
        "REGIONAL" === userData.type &&
        [
          "COMERCIAL",
          "PARCEIRO_MASTER",
          "PARCEIRO_VENDEDOR",
          "PARCEIRO_OPERACIONAL"
        ].includes(usuarioDialog.type as string)
      ) {
        return true;
      } else if (
        "COMERCIAL" === userData.type &&
        [
          "PARCEIRO_MASTER",
          "PARCEIRO_VENDEDOR",
          "PARCEIRO_OPERACIONAL"
        ].includes(usuarioDialog.type as string)
      ) {
        return true;
      } else if (
        "PARCEIRO_MASTER" === userData.type &&
        ["PARCEIRO_VENDEDOR", "PARCEIRO_OPERACIONAL"].includes(
          usuarioDialog.type as string
        )
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const changeEnableState = async (row: UserListRow) => {
    openModalConfirm({
      onConfirm: async () => {
        try {
          const { data } = await apiJWT.patch(`/usuarios/${row.id}/enabled`, {
            enabled: !row.enabled
          });
          updateRow(data);
        } catch (error: any) {
          Toast({
            title: getErrorMessage({
              error,
              defaultMessage: `Erro ao ${
                !row.enabled ? " Ativar" : " Desativar"
              } usuário`
            })
          });
        }
      },
      message: (
        <Box w="100%">
          <Text>
            Deseja <b>{!row.enabled ? "Ativar" : "Desativar"}</b> o usuário?
          </Text>
        </Box>
      )
    });
  };

  const columns: Column[] = [
    {
      name: "Ativo",
      render: (row: UserListRow) => {
        if (row.enabled === true) return "SIM";
        if (row.enabled === false) return "NÃO";
      },
      cellStyle: (row) => ({ opacity: row.enabled ? 1 : 0.4 })
    },
    {
      name: "Usuário",
      key: "username",
      cellStyle: (row) => ({ opacity: row.enabled ? 1 : 0.4 })
    },
    {
      name: "Nome",
      key: "name",
      cellStyle: (row) => ({ opacity: row.enabled ? 1 : 0.4 })
    },
    {
      name: "Tipo",
      render: (row: UserListRow) => getDescOfType(row.type as string),
      key: "type",
      cellStyle: (row) => ({ opacity: row.enabled ? 1 : 0.4 })
    },
    {
      name: "Empresa",
      render: (row: UserListRow) => {
        return row.customer.name;
      },
      cellStyle: (row) => ({ opacity: row.enabled ? 1 : 0.4 })
    },
    {
      name: "Ações",
      render: (row: UserListRow) => {
        return (
          <>
            <Tooltip label={row.enabled ? "Desativar" : "Ativar"}>
              <IconButton
                aria-label=""
                icon={<FaPowerOff />}
                mr="8px"
                onClick={() => changeEnableState(row)}
              />
            </Tooltip>
            <Tooltip label="Editar">
              <IconButton
                aria-label=""
                icon={<EditIcon />}
                mr="8px"
                onClick={() => editUsuario(row)}
              />
            </Tooltip>
            <Tooltip label="Redefinir Senha">
              <IconButton
                aria-label=""
                icon={<FaKey />}
                mr="8px"
                onClick={() => editUsuarioPassword(row)}
              />
            </Tooltip>

            <Tooltip label="Excluir">
              <IconButton
                aria-label=""
                icon={<DeleteIcon />}
                variant="danger"
                onClick={() =>
                  openModalConfirm({
                    message: `Deseja excluir ${row.username} ("${row.name})" ?`,
                    title: "Confirmação",
                    onConfirm: () => acceptDeleteUsuario(row),
                    confirmButtonStyle: {
                      variant: "danger",
                      leftIcon: <DeleteIcon />,
                      loadingText: "Deletando"
                    }
                  })
                }
              />
            </Tooltip>
          </>
        );
      }
    }
  ];

  return (
    <GetLayout title="Usuários">
      <Grid mb="30px" templateColumns="1fr 1fr 1fr" gap="16px">
        <Box>
          <InputField
            title="Filtrar por Nome ou Usuário"
            value={filtros.nomeOrCodigo || ""}
            onChange={(e) => onChangeFiltro("nomeOrCodigo", e)}
          />
        </Box>
        <Box>
          <Text mb="8px">&nbsp;</Text>
          <Button
            w="100%"
            leftIcon={<AddIcon />}
            onClick={openDialogNewUsuario}
            isDisabled={!customers.length}
          >
            Adicionar Usuário
          </Button>
        </Box>
        <Box>
          <Text mb="8px">&nbsp;</Text>
          <Button
            w="100%"
            isLoading={loading}
            loadingText="Atualizando"
            leftIcon={<RepeatIcon />}
            onClick={atualizar}
          >
            Atualizar
          </Button>
        </Box>
      </Grid>
      <DynamicTable
        rows={getFilteredUsuarios()}
        columns={columns}
        isLoading={loading}
      />
      <CustomModal
        isOpen={openUsuarioPasswordDialog}
        size="md"
        modalTitle="Redefinir Senha"
        modalFooter={usuarioPasswordDialogFooter}
        onClose={hideUsuarioPasswordDialog}
      >
        <Box>
          <InputField
            title="Nova senha"
            value={usuarioPasswordDialog.password}
            errorMessage={errors.password ? "Informe a nova senha" : undefined}
            onChange={(e) =>
              onChangeUsuarioPasswordDialog("password", e.target.value)
            }
          />
        </Box>
      </CustomModal>

      <CustomModal
        isOpen={openUsuarioDialog}
        size="2xl"
        modalTitle="Usuários"
        modalFooter={usuarioDialogFooter}
        onClose={onCloseUsuarioModal}
      >
        <Grid gap="16px" templateColumns="1fr 1fr" mb="10px">
          <Box gridArea={!isUpdate ? "auto / span 2" : ""}>
            <DropdownField
              title="Empresa"
              value={usuarioDialog.customer?.id}
              options={customers.filter((customer) => {
                if (userData.type === "COMERCIAL" && !isUpdate)
                  return customer.id !== userData.customerId;
                return true;
              })}
              dropdownProps={{
                w: "100%",
                optionValue: "id",
                optionLabel: "name",
                isDisabled: isUpdate || isParceiroMaster
              }}
              onChange={(value) => onChangeUsuarioDialog("customer", value)}
              errorMessage={
                errors.customer ? "Selecione uma empresa" : undefined
              }
            />
          </Box>
          <Box>
            <InputField
              title="Nome"
              value={usuarioDialog.name}
              errorMessage={errors.name ? "Informe um nome" : undefined}
              onChange={(e) => onChangeUsuarioDialog("name", e.target.value)}
              isDisabled={!canEdit()}
            />
          </Box>
          <Box>
            <InputField
              title="Usuário"
              value={usuarioDialog.username}
              errorMessage={errors.username ? "Informe um usuário" : undefined}
              onChange={(e) =>
                onChangeUsuarioDialog("username", e.target.value)
              }
              isDisabled={!canEdit()}
            />
          </Box>

          {!isUpdate && (
            <Box>
              <InputField
                title="Senha"
                value={usuarioDialog.password || ""}
                errorMessage={errors.password ? "Informe uma senha" : undefined}
                onChange={(e) =>
                  onChangeUsuarioDialog("password", e.target.value)
                }
              />
            </Box>
          )}

          <Box>
            <DropdownField
              title="Tipo"
              dropdownProps={{ w: "100%", isDisabled: !canEdit() }}
              value={usuarioDialog.type}
              onChange={(value) => onChangeUsuarioDialog("type", value)}
              options={getDropdownUserType()}
              errorMessage={errors.type ? "Selecione um tipo" : undefined}
            />
          </Box>
        </Grid>
      </CustomModal>
      <ModalComDropdownRecorrencia />
    </GetLayout>
  );
};

export default Usuarios;

export const setRoutePermissions: RoutePermissionFunction = (route) => {
  route.setUserTypesPermission([
    "SUPER",
    "REGIONAL",
    "COMERCIAL",
    "PARCEIRO_GERENTE_ESTRATEGICO",
    "PARCEIRO_GERENTE_VAREJO",
    "PARCEIRO_MASTER",
    "ASCOM"
  ]);
  route.setColomboUserTypesPermission([
    "COMERCIAL",
    "PARCEIRO_GERENTE_ESTRATEGICO",
    "PARCEIRO_GERENTE_VAREJO",
    "PARCEIRO_MASTER"
  ]);
};
